import type { Habit, HabitId } from "../../../shared/types";
import { getCategoryLabel, getCategoryBarColor } from "../../../shared/stores/category-store";
import { getMonthYear, getToday } from "../../../shared/types/date-helpers";

interface HabitWeeklyViewProps {
  readonly habits: Habit[];
  readonly weekDays: string[];
  readonly onToggle: (id: HabitId, date: string) => void;
  readonly onEdit: (habit: Habit) => void;
  readonly onPrevWeek: () => void;
  readonly onNextWeek: () => void;
}

export function HabitWeeklyView({
  habits,
  weekDays,
  onToggle,
  onEdit,
  onPrevWeek,
  onNextWeek,
}: HabitWeeklyViewProps) {
  const firstDay = weekDays[0];
  const monthYear = getMonthYear(firstDay);
  const today = getToday();

  return (
    <div className="flex flex-col gap-3">
      {/* Navigation semaine compacte */}
      <div className="flex items-center justify-between px-2">
        <button
          type="button"
          onClick={onPrevWeek}
          className="shrink-0 min-w-8 min-h-8 flex items-center justify-center text-ink-faint hover:text-ink rounded-lg hover:bg-surface-alt/50 transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <p className="text-sm font-semibold text-ink">{monthYear}</p>
        <button
          type="button"
          onClick={onNextWeek}
          className="shrink-0 min-w-8 min-h-8 flex items-center justify-center text-ink-faint hover:text-ink rounded-lg hover:bg-surface-alt/50 transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* Liste des habitudes avec grille de jours */}
      <div className="flex flex-col gap-2">
        {habits.map((habit) => {
          const weekCount = weekDays.filter((d) => habit.completedDates.includes(d)).length;
          return (
            <div
              key={habit.id}
              className="relative border-r border-border/40 transition-all duration-300 hover:bg-surface-alt/30"
            >
              <div
                className={`absolute left-0 top-0 bottom-0 w-0.5 rounded-full ${getCategoryBarColor(habit.category)}`}
              />
              <div className="flex items-center gap-3 pl-3 pr-2 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink">{habit.title}</p>
                  <p className="text-xs text-ink-faint">
                    {getCategoryLabel(habit.category)}
                    <span className="text-ink-faint/30 mx-1">·</span>
                    <span className={weekCount > 0 ? "text-accent font-semibold" : ""}>
                      {weekCount}/7
                    </span>
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`edit-habit-${habit.id}`}
                  onClick={() => onEdit(habit)}
                  aria-label={`Modifier ${habit.title}`}
                  className="shrink-0 min-w-8 min-h-8 flex items-center justify-center text-ink-faint hover:text-ink rounded-lg hover:bg-surface-alt/50 transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-4 h-4 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 px-2 pb-2">
                {weekDays.map((date) => {
                  const isDone = habit.completedDates.includes(date);

                  // Pour les habitudes hebdomadaires : vérifier si déjà validée cette semaine
                  const isWeekly = habit.frequency === "weekly";
                  const weeklyDoneInWeek = isWeekly
                    ? weekDays.some((d) => habit.completedDates.includes(d))
                    : false;
                  const isToday = date === today;
                  const isFuture = date > today;
                  const isDisabled = isFuture || (isWeekly && weeklyDoneInWeek && !isDone);

                  const dayName = new Date(date + "T00:00:00").toLocaleDateString(
                    "fr-FR",
                    { weekday: "short" },
                  );
                  return (
                    <button
                      key={date}
                      type="button"
                      onClick={() => !isDisabled && onToggle(habit.id, date)}
                      disabled={isDisabled}
                      className={`flex flex-col items-center gap-0.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                        isToday ? "ring-2 ring-accent" : ""
                      } ${
                        isDone
                          ? "bg-green-500 text-white"
                          : isFuture
                            ? "bg-surface-alt/40 text-ink-faint/30 cursor-not-allowed"
                            : isDisabled
                              ? "bg-green-500/20 text-green-600/60 cursor-not-allowed"
                              : isToday
                                ? "bg-accent/15 text-accent hover:bg-accent/25"
                                : "bg-surface-alt text-ink-faint hover:bg-border/60"
                      }`}
                      aria-label={`${dayName} ${date.split("-")[2]}: ${isDone ? "complété" : isFuture ? "jour futur" : isDisabled ? "déjà fait cette semaine" : "non complété"}`}
                    >
                      <span className="text-[9px] font-normal opacity-70">
                        {dayName.charAt(0)}
                      </span>
                      <span>{date.split("-")[2]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
